import { useMemo } from 'react';
import { useAdCampaignsDataContext } from 'src/contexts/AdCampaignsDataContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { useCampaignsTableMetricsData } from 'src/tables/SponsoredListings/CampaignsTableV2/useCampaignsTableMetricsData';
import { formatCurrency } from 'src/utils/numberFormatter';
import { DateTimeRange, SponsoredListingsCampaign } from 'src/WebApiController';

import { formatCompactCurrency } from '../CampaignStatsRollup/CampaignStatsRollup';
import { computeChartData } from './PerformanceChart';
import * as styles from './PerformanceMobileTile.css';

type TileProps = {
  title: string;
  value: React.ReactNode;
  accent?: boolean;
};

function Tile({ title, value, accent }: TileProps) {
  return (
    <div className={`${styles.tile}${accent ? ` ${styles.tileAccent}` : ''}`}>
      <div className={styles.tileTitle}>{title}</div>
      <div className={styles.tileValue}>{value}</div>
    </div>
  );
}

type PerformanceMobileTileProps = {
  allCampaigns: SponsoredListingsCampaign[] | null;
  dateRangeFilter: DateTimeRange | null;
  currencyCode?: string;
};

export function PerformanceMobileTile({
  allCampaigns,
  dateRangeFilter,
  currencyCode = 'USD',
}: PerformanceMobileTileProps) {
  const { adCampaignsDataQuery } = useAdCampaignsDataContext();

  // Populate flight metrics (salesProceeds, flightSpend) the same way PerformanceChart does.
  const { campaigns: campaignsWithMetrics, isLoading: metricsLoading } =
    useCampaignsTableMetricsData(allCampaigns, dateRangeFilter);

  // `adCampaignsDataQuery` is `{}` (the default context value) when the user is
  // missing AdPlatform view permission — in that case the provider skips wiring
  // up the real query, so guard for it.
  const isLoading = Boolean(adCampaignsDataQuery?.isLoading) || metricsLoading;

  const { todayRevenue, rangeRevenue } = useMemo(() => {
    const { data } = computeChartData(campaignsWithMetrics, dateRangeFilter);
    // computeChartData buckets flights by YYYY-MM-DD UTC dateKey.
    const todayKey = new Date().toISOString().slice(0, 10);
    let today = 0;
    let total = 0;
    for (const point of data) {
      total += point.revenue;
      if (point.dateKey === todayKey) {
        today += point.revenue;
      }
    }
    return { todayRevenue: today, rangeRevenue: total };
  }, [campaignsWithMetrics, dateRangeFilter]);

  const spinner = (
    <span className={styles.spinnerWrapper}>
      <PosSpinner size={vars.iconSize.s} centered={false} />
    </span>
  );

  return (
    <div className={styles.container}>
      <div className={styles.title}>Performance</div>
      <div className={styles.tilesRow}>
        <Tile
          title="Sponsored Revenue Today"
          value={
            isLoading
              ? spinner
              : todayRevenue > 0
                ? formatCompactCurrency(todayRevenue, currencyCode)
                : formatCurrency(0, currencyCode, 0)
          }
          accent
        />
        <Tile
          title="Sponsored Revenue (Last 30 Days)"
          value={
            isLoading
              ? spinner
              : rangeRevenue > 0
                ? formatCompactCurrency(rangeRevenue, currencyCode)
                : formatCurrency(0, currencyCode, 0)
          }
        />
      </div>
    </div>
  );
}
